import type { OptimizelyDecision } from '@optimizely/react-sdk'

import type { DashboardExperienceApiVersion } from '../configs'

export const DASHBOARD_EXPERIENCE_V2_FEATURE_FLAG_KEY =
  'rockets_ofx_webclient_dashboard_experience_v2_feature'

export const DASHBOARD_EXPERIENCE_TELEMETRY_EVENTS = {
  pageLanded: 'dashboard_experience_page_landed',
  experimentAllocated: 'dashboard_experience_experiment_allocated',
  variantViewed: 'dashboard_experience_variant_viewed',
  apiSuccess: 'dashboard_experience_api_success',
  uiRendered: 'dashboard_experience_ui_rendered',
} as const

export type DashboardExperienceVariant = 'control' | 'variant'

export type DashboardExperienceTelemetryContext = {
  page: 'MyHomePage'
  apiVersion: DashboardExperienceApiVersion
  dashboardExperienceVariant: DashboardExperienceVariant
  flagKey: string
  variationKey: string
  experimentEnabled: boolean
  decisionReady: boolean
  decisionTimedOut: boolean
  allocationId?: string
}

export const getDashboardExperienceVariantFromApiVersion = (
  apiVersion: DashboardExperienceApiVersion,
): DashboardExperienceVariant => (apiVersion === 'v2' ? 'variant' : 'control')

const getAllocationIdFromDecision = (decision: OptimizelyDecision): string | undefined => {
  const userContext = decision.userContext as { id?: unknown } | undefined

  return typeof userContext?.id === 'string' ? userContext.id : undefined
}

export const getDashboardExperienceTelemetryContext = ({
  apiVersion,
  decision,
  hasDecisionTimedOut,
  isDecisionReady,
}: {
  apiVersion: DashboardExperienceApiVersion
  decision: OptimizelyDecision
  hasDecisionTimedOut: boolean
  isDecisionReady: boolean
}): DashboardExperienceTelemetryContext => {
  const allocationId = getAllocationIdFromDecision(decision)

  return {
    page: 'MyHomePage',
    apiVersion,
    dashboardExperienceVariant: getDashboardExperienceVariantFromApiVersion(apiVersion),
    flagKey: decision.flagKey || DASHBOARD_EXPERIENCE_V2_FEATURE_FLAG_KEY,
    variationKey: decision.variationKey ?? 'unknown',
    experimentEnabled: decision.enabled === true,
    decisionReady: isDecisionReady,
    decisionTimedOut: hasDecisionTimedOut,
    ...(allocationId ? { allocationId } : {}),
  }
}

export const getDashboardExperienceTelemetryTrackingKey = ({
  allocationId,
  apiVersion,
  flagKey,
  variationKey,
}: DashboardExperienceTelemetryContext) =>
  [flagKey, allocationId ?? 'unknown-allocation', variationKey, apiVersion].join(':')
